import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { Icon } from '@/components/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import {
  BlockContainer,
  BlockTitleContainer,
} from './FinancesQuestionForm.styled';

type TotalLineProps = {
  label: ReactNode;
  value: ReactNode;
};

export function TotalLine({ label, value }: Readonly<TotalLineProps>) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="neutral.700">
        {label}
      </Typography>
      <Typography variant="body2Medium">{value}</Typography>
    </Box>
  );
}

type AddBlockButtonProps = {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  icon?: ReactNode;
};

export function AddBlockButton({
  label,
  onClick,
  disabled,
  icon = <Icon name="plus" />,
}: Readonly<AddBlockButtonProps>) {
  return (
    <Box display="flex" justifyContent="flex-end" mb={2}>
      <Button
        variant="text"
        startIcon={icon}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
    </Box>
  );
}

type FinanceBlockProps = {
  title: string;
  counter?: string;
  onDelete?: () => void;
  isDeletable?: boolean;
  children: ReactNode;
};

export function FinanceBlock({
  title,
  counter,
  onDelete,
  isDeletable = false,
  children,
}: Readonly<FinanceBlockProps>) {
  const { t } = useTranslate();

  return (
    <BlockContainer>
      <BlockTitleContainer>
        <Typography variant="body1Medium" color="neutral.1000">
          {title}{' '}
          {counter && (
            <Typography variant="body1" component="span" color="neutral.800">
              {counter}
            </Typography>
          )}
        </Typography>
        {isDeletable && (
          <IconButton
            onClick={onDelete}
            sx={{ width: 24, height: 24 }}
            title={t('moreInfo.form.yourFinances.button.delete')}
          >
            <Icon name="delete" size={16} />
          </IconButton>
        )}
      </BlockTitleContainer>
      {children}
    </BlockContainer>
  );
}

type CharactersLeftProps = {
  count: number;
};

export function CharactersLeft({ count }: Readonly<CharactersLeftProps>) {
  return (
    <Typography variant="caption" color="neutral.700" mt={1} textAlign="right">
      <Trans
        i18nKey="moreInfo.form.yourFinances.charactersLeft"
        values={{ count }}
        components={{
          bold: (
            <Typography
              component="span"
              variant="captionBold"
              color="neutral.1000"
            />
          ),
        }}
      />
    </Typography>
  );
}
type InfoHintProps = {
  text: ReactNode;
};

export function InfoHint({ text }: InfoHintProps) {
  return (
    <Stack direction="row" alignItems="center" gap={0.5} mt={1}>
      <Icon
        name="info-outline"
        size={16}
        color={theme => theme.palette.neutral[700]}
      />
      <Typography variant="caption" color="neutral.700">
        {text}
      </Typography>
    </Stack>
  );
}
